import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { showToasterMessage } from 'src/app/shared/actions/toaster.actions';
import { downloadBlob } from 'src/app/shared/utilities/download-blob';
import { DocumentTypeSummaryBase } from '../../../shared/lib/compliance-shared/models/document-type-summary-base.model';
import { PagingTokenFixed } from '../../../shared/models/core';
import { DocumentTypeActions } from '../actions';
import * as fromSelectors from '../doc-admin.selector';
import { DocumentAdminClient } from 'src/app/compliance/api/document-admin-client.service';
import { DocumentTypesClient } from 'src/app/compliance/api/document-types-client.service';
import { DocAdminHelperService } from 'src/app/compliance/requirements-admin/doc-admin-helper.service';
import { gridDate } from 'src/app/compliance/api/migration/grid-date.function';
import { PagingTokenOfDocumentAdminSummaryDto } from 'src/app/compliance/api/models/paging-token-of-document-admin-summary-dto.model';
import { AddDocumentTypeRequest } from 'src/app/compliance/api/models/add-document-type-request.model';
import { UpdateDocumentTypeRequest } from 'src/app/compliance/api/models/update-document-type-request.model';
import { AddDocumentTypeCodeRequest } from 'src/app/compliance/api/models/add-document-type-code-request.model';
import { UpdateDocumentTypeCodeRequest } from 'src/app/compliance/api/models/update-document-type-code-request.model';
import type { DocumentType } from 'src/app/compliance/models/document-type';

@Injectable()
export class DocumentTypeEffects {
    constructor(
        private readonly _actions$: Actions,
        private readonly _docAdminService: DocumentAdminClient,
        private readonly _docAdminTypeClient: DocumentTypesClient,
        private readonly _docAdminHelperClient: DocAdminHelperService,
        private readonly _store: Store,
        private readonly _router: Router
    ) {}

    loadDocuments$ = createEffect(() =>
        this._actions$.pipe(
            ofType(
                DocumentTypeActions.DocumentTypes.getDocumentTypes,
                DocumentTypeActions.DocumentTypes.setDocumentTypesFilter
            ),
            concatLatestFrom(() => this._store.select(fromSelectors.selectFilter)),
            switchMap(([action, filters]) => {
                const { filter } = filters;
                return this._docAdminService
                    .getDocumentAdmin(
                        filter.id, // filter_Id: number | null | undefined,
                        filter.facilityId, // filter_FacilityId: number | null | undefined
                        filter.facilityName, // filter_FacilityName: string | null | undefined
                        filter.associationId, // filter_AssociationId: number | null | undefined
                        filter.associationName, // filter_AssociationName: string | null | undefined
                        filter.stateId, // filter_StateId: number | null | undefined
                        filter.stateName, // filter_StateName: string | null | undefined
                        filter.name, // filter_Name: string | null | undefined
                        filter.candidateTaskTypeName, // filter_CandidateTaskTypeName: string | null | undefined
                        filter.candidateTaskTypeId, // filter_CandidateTaskTypeId: number[] | null | undefined
                        filter.qmDefault, // filter_QmDefault: boolean | null | undefined
                        filter.requiresExpiration, // filter_RequiresExpiration: boolean | null | undefined
                        filter.category, // filter_Category: string[] | null | undefined
                        gridDate(filter.lastModified), // filter_LastModified: Date | null | undefined
                        filter.linesOfBusiness, // filter_LinesOfBusiness: number[] | null | undefined
                        filter.categoryLineOfBusinessId, // filter_CategoryLineOfBusinessId: number[] | null | undefined
                        filter.requirementLevel, // filter_RequirementLevel: DocumentTypeLevelType | undefined
                        filter.acceptanceCriteriaType, // filter_AcceptanceCriteriaType: AcceptanceCriteriaType2[] | null | undefined
                        filter.acceptanceCriteriaStatus, // filter_AcceptanceCriteriaStatus: AcceptanceCriteriaStatus2[] | null | undefined
                        filters.pageSize,
                        filters.skip,
                        filters.sort?.field,
                        filters.sort?.dir as any,
                        undefined
                    )
                    .pipe(
                        map((documents: PagingTokenOfDocumentAdminSummaryDto) =>
                            DocumentTypeActions.DocumentTypes.getDocumentTypesSuccess({
                                documents: documents as unknown as PagingTokenFixed<DocumentTypeSummaryBase[]>
                            })
                        ),
                        catchError((error) => of(DocumentTypeActions.DocumentTypes.getDocumentTypesFailure({ error })))
                    );
            })
        )
    );

    downloadcDocuments$ = createEffect(() =>
        this._actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypes.downloadDocumentTypes),
            switchMap((action) =>
                this._docAdminService.downloadFile(action.documentId).pipe(
                    map((x) => x?.data),
                    map((blob) => {
                        downloadBlob(blob, action.fileName);
                        return DocumentTypeActions.DocumentTypes.downloadDocumentTypesSuccess({
                            blob,
                            fileName: action.fileName
                        });
                    }),
                    catchError((error) => of(DocumentTypeActions.DocumentTypes.downloadDocumentTypesFailure({ error })))
                )
            )
        )
    );

    loadDocumentsTypes$ = createEffect(() =>
        this._actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypes.getDocumentTypesList),
            switchMap(() =>
                this._docAdminHelperClient.getDocumentTypesList().pipe(
                    map((documentTypesList) =>
                        DocumentTypeActions.DocumentTypes.getDocumentTypesListSuccess({
                            documentTypesList: (documentTypesList as any[]).map((item: any) => ({
                                id: item.id,
                                categoryId: item.categoryId,
                                category: item.category,
                                liaisonUserName: item.liaisonUserName,
                                ...item
                            })) as DocumentType[]
                        })
                    ),
                    catchError((error) => of(DocumentTypeActions.DocumentTypes.getDocumentTypesListFailure({ error })))
                )
            )
        )
    );

    loadDocumentsTypesByName$ = createEffect(() =>
        this._actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypes.getDocumentTypesListByName),
            switchMap((action) =>
                this._docAdminHelperClient.getDocumentTypesList(action.name).pipe(
                    map((documentTypesList) =>
                        DocumentTypeActions.DocumentTypes.getDocumentTypesListSuccess({
                            documentTypesList: (documentTypesList as any[]).map((item: any) => ({
                                id: item.id,
                                categoryId: item.categoryId,
                                category: item.category,
                                liaisonUserName: item.liaisonUserName,
                                ...item
                            })) as DocumentType[]
                        })
                    ),
                    catchError((error) => of(DocumentTypeActions.DocumentTypes.getDocumentTypesListFailure({ error })))
                )
            )
        )
    );

    loadDocumentsTypesById$ = createEffect(() =>
        this._actions$.pipe(
            ofType(
                DocumentTypeActions.DocumentTypes.getDocumentType,
                DocumentTypeActions.DocumentTypes.addSubTypeSuccess,
                DocumentTypeActions.DocumentTypes.addDocCodeSuccess,
                DocumentTypeActions.DocumentTypes.editDocCodeSuccess,
                DocumentTypeActions.DocumentTypes.deleteDocCodeSuccess
            ),
            switchMap((action) =>
                this._docAdminTypeClient.getDocumentTypeById(action.id).pipe(
                    map((documentType) => DocumentTypeActions.DocumentTypes.getDocumentTypeSuccess({ documentType })),
                    catchError((error) => of(DocumentTypeActions.DocumentTypes.getDocumentTypeFailure({ error })))
                )
            )
        )
    );

    createDocumentType$ = createEffect(() =>
        this._actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypes.createDocumentType),
            switchMap((action) =>
                this._docAdminTypeClient.createDocumentType(action.documentType as AddDocumentTypeRequest).pipe(
                    map((document) => DocumentTypeActions.DocumentTypes.goToDocumentPage({ documentId: document.id })),
                    catchError((error) => of(DocumentTypeActions.DocumentTypes.createDocumentTypeFailure({ error })))
                )
            )
        )
    );

    updateDocumentType$ = createEffect(() =>
        this._actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypes.updateDocumentType),
            switchMap((action) =>
                this._docAdminTypeClient
                    .updateDocumentType(action.documentId, action.documentType as UpdateDocumentTypeRequest)
                    .pipe(
                        map((document) =>
                            DocumentTypeActions.DocumentTypes.goToDocumentPage({ documentId: document.id })
                        ),
                        catchError((error) =>
                            of(DocumentTypeActions.DocumentTypes.createDocumentTypeFailure({ error }))
                        )
                    )
            )
        )
    );

    addSubType$ = createEffect(() =>
        this._actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypes.addSubType),
            switchMap((action) =>
                this._docAdminTypeClient.addDocumentTypeCodeSet(action.id, { name: action.name }).pipe(
                    map((document) => DocumentTypeActions.DocumentTypes.addSubTypeSuccess({ id: action.id })),
                    catchError((error) => of(DocumentTypeActions.DocumentTypes.addSubTypeFailure({ error })))
                )
            )
        )
    );

    addDocCode$ = createEffect(() =>
        this._actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypes.addDocCode),
            switchMap((action) =>
                this._docAdminTypeClient
                    .addDocumentTypeCodes(action.id, action.newDocCode as AddDocumentTypeCodeRequest)
                    .pipe(
                        map((document) => DocumentTypeActions.DocumentTypes.addDocCodeSuccess({ id: action.id })),
                        catchError((error) => of(DocumentTypeActions.DocumentTypes.addDocCodeFailure({ error })))
                    )
            )
        )
    );

    editDocCode$ = createEffect(() =>
        this._actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypes.editDocCode),
            switchMap((action) =>
                this._docAdminTypeClient
                    .updateDocumentTypeCode(
                        action.id,
                        action.codeId,
                        action.newDocCode as UpdateDocumentTypeCodeRequest
                    )
                    .pipe(
                        map((document) => DocumentTypeActions.DocumentTypes.editDocCodeSuccess({ id: action.id })),
                        catchError((error) => of(DocumentTypeActions.DocumentTypes.editDocCodeFailure({ error })))
                    )
            )
        )
    );

    goToDocumentPage$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(DocumentTypeActions.DocumentTypes.goToDocumentPage),
                map((action) => {
                    this._router.navigate([`/compliance/per-diem-doc-admin/${action.documentId}`]);
                })
            ),
        { dispatch: false }
    );

    uploadSupplementalFile$ = createEffect(() =>
        this._actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypes.uploadSupplementalFile),
            switchMap((action) =>
                this._docAdminHelperClient.uploadSupplementalFile(action.file).pipe(
                    map((file) => DocumentTypeActions.DocumentTypes.uploadSupplementalFileSuccess({ file })),
                    catchError((error) =>
                        of(DocumentTypeActions.DocumentTypes.uploadSupplementalFileFailure({ error }))
                    )
                )
            )
        )
    );

    deleteDocCode$ = createEffect(() =>
        this._actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypes.deleteDocCode),
            switchMap((action) =>
                this._docAdminTypeClient.removeDocumentTypeCode(action.id, action.codeId).pipe(
                    map((document) => DocumentTypeActions.DocumentTypes.deleteDocCodeSuccess({ id: action.id })),
                    catchError((error) => of(DocumentTypeActions.DocumentTypes.deleteDocCodeFailure({ error })))
                )
            )
        )
    );

    showUploadSuccessMessage$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(DocumentTypeActions.DocumentTypes.uploadSupplementalFileSuccess),
                map(() => {
                    this._store.dispatch(
                        showToasterMessage({
                            message: 'File upload success'
                        })
                    );
                })
            ),
        { dispatch: false }
    );

    showUploadFailureMessage$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(DocumentTypeActions.DocumentTypes.uploadSupplementalFileFailure),
                map(() => {
                    this._store.dispatch(
                        showToasterMessage({
                            message: 'File upload failure'
                        })
                    );
                })
            ),
        { dispatch: false }
    );

    createSubtypeFailure$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    DocumentTypeActions.DocumentTypes.addSubTypeFailure,
                    DocumentTypeActions.DocumentTypes.addDocCodeFailure
                ),
                map(() => {
                    this._store.dispatch(
                        showToasterMessage({
                            message: 'Add Action Failure'
                        })
                    );
                })
            ),
        { dispatch: false }
    );

    deleteDocCodeFailure$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(DocumentTypeActions.DocumentTypes.deleteDocCodeFailure),
                map(() => {
                    this._store.dispatch(
                        showToasterMessage({
                            message: 'Delete Doc Code Failure'
                        })
                    );
                })
            ),
        { dispatch: false }
    );

    displayToaster$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    DocumentTypeActions.DocumentTypes.createDocumentTypeFailure,
                    DocumentTypeActions.DocumentTypes.editDocCodeFailure
                ),
                map((action) =>
                    this._store.dispatch(
                        showToasterMessage({ message: 'Error: ' + action.error.error.error.message, duration: 3000 })
                    )
                )
            ),
        { dispatch: false }
    );

    displayToasterSuccess$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(DocumentTypeActions.DocumentTypes.goToDocumentPage),
                map((action) => this._store.dispatch(showToasterMessage({ message: 'Document type created success' })))
            ),
        { dispatch: false }
    );
}
